import {Box} from '@chakra-ui/layout';
import {
	Center,
	Collapse,
	Flex,
	FormControl,
	FormHelperText,
	FormLabel,
	Progress,
	RadioGroup,
	Stack
} from '@chakra-ui/react';
import {GeoLink} from 'components/action/GeoLink';
import {FormStatusError} from 'components/ApiError';
import {matched, required} from 'components/form';
import {GeoCheckbox, GeoRadio} from 'components/forms';
import {GeoButton} from 'components/forms/GeoButton';
import {GeoHiddenField} from 'components/forms/GeoHiddenField';
import {GeoInput} from 'components/forms/GeoInput';
import {GeoHeaderText} from 'components/text/GeoHeaderText';
import {GeoSubheaderText} from 'components/text/GeoSubheaderText';
import {GeoSubtitleText} from 'components/text/GeoSubtitleText';
import {useTranslation} from 'modules/i18n';
import React from 'react';
import {useFormContext} from 'react-hook-form';
import {Form} from 'utils/form';
import {useResponsive} from 'utils/useResponsive';
import {LocalSignupForm, useSignup} from './hooks';

const SignupFormContent: React.FC<{error: any; loading: boolean}> = ({
	loading,
	error
}) => {
	const {isMobile} = useResponsive();
	const {t} = useTranslation();
	const {setValue, trigger, watch} = useFormContext<LocalSignupForm>();
	const _step = watch('step');
	const _accType = watch('accType');
	const _password = watch('password');
	const _passwordMedium = new RegExp(
		/(?=(.*[0-9]))((?=.*[A-Za-z0-9])(?=.*[A-Z])(?=.*[a-z]))^.{8,}$/
	);
	const _passwordStrong = new RegExp(
		/(?=(.*[0-9]))(?=.*[\!@#$%^&*()\\[\]{}\-_+=~`|:;"'<>,./?])(?=.*[a-z])(?=(.*[A-Z]))(?=(.*)).{8,}/
	);

	const renderPasswordStrength = () => {
		const isStrong = _passwordStrong.test(_password);
		const isMedium = _passwordMedium.test(_password);

		if (_password) {
			return (
				<>
					<Progress
						marginTop={2}
						value={isStrong ? 100 : isMedium ? 66 : 33}
						size='xs'
						colorScheme={isStrong ? 'green' : isMedium ? 'orange' : 'red'}
						borderRadius={5}
					/>
				</>
			);
		}

		return null;
	};

	const renderButtonNext = () => {
		return (
			<Stack direction={isMobile ? 'column-reverse' : 'row'} spacing='3'>
				<Box>
					<GeoButton
						id='buttonNext'
						onClick={async () => {
							const _testUsername = await trigger('username');
							const _testPassword = await trigger('password');
							const _testPasswordConfirm = await trigger('passwordConfirm');
							if (_testUsername && _testPassword && _testPasswordConfirm) {
								setValue('step', 'profile');
							}
						}}
						data-cy='button-next'
					>
						{t('auth.next')}
					</GeoButton>
				</Box>
			</Stack>
		);
	};

	const renderPositionNext = () => {
		return isMobile ? (
			<Center pt={4}>{renderButtonNext()}</Center>
		) : (
			<Flex justifyContent='flex-end' pt={4}>
				{renderButtonNext()}
			</Flex>
		);
	};

	const renderGroupButton = () => {
		return (
			<Stack direction={isMobile ? 'column-reverse' : 'row'} spacing='3'>
				<Box>
					<GeoButton
						id='signUp'
						isLoading={loading}
						data-cy='button-submit'
						type='submit'
					>
						{t('auth.createAccount')}
					</GeoButton>
				</Box>
			</Stack>
		);
	};
	const renderPosition = () => {
		return isMobile ? (
			<Center pt={4}>{renderGroupButton()}</Center>
		) : (
			<Flex justifyContent='flex-end' pt={4}>
				{renderGroupButton()}
			</Flex>
		);
	};

	return (
		<Stack spacing={3} direction='column'>
			<GeoSubheaderText>{t('auth.subHeaderLogin')}</GeoSubheaderText>
			<GeoHeaderText>{t('auth.signUp')}</GeoHeaderText>
			<Box marginTop={2}>
				<Stack spacing={2} direction='row'>
					<GeoSubtitleText>{t('auth.askIfAccount')}</GeoSubtitleText>
					<GeoLink
						color='geoBlue.500'
						label={t('auth.signIn')}
						to='authLogin'
						data-cy='link-login'
					/>
				</Stack>
			</Box>
			<Box marginTop={5}>
				<GeoInput
					name='username'
					type='email'
					label={t('auth.email')}
					data-cy='input-username'
					placeholder={t('auth.placeholderEmail')}
					validation={required('errors.required')}
				/>
			</Box>
			<Box marginTop={4}>
				<FormControl>
					<>
						<GeoInput
							name='password'
							type='password'
							label={t('auth.password')}
							data-cy='input-password'
							placeholder={t('auth.placeholderPassword')}
							validation={required('errors.required')}
						/>
						{renderPasswordStrength()}
						{_password === undefined || _password.length >= 8 ? null : (
							<FormHelperText fontSize={'0.8rem'} paddingLeft={4}>
								{t('auth.passwordRequirements')}
							</FormHelperText>
						)}
					</>
				</FormControl>
			</Box>
			<Box marginTop={4}>
				<GeoInput
					name='passwordConfirm'
					label={t('auth.repassword')}
					type='password'
					data-cy='input-passwordConfirm'
					validation={matched('signup.errors.mismatched')}
					placeholder={t('auth.placeholderRePassword')}
				/>
			</Box>
			<Stack />
			{_step === 'profile' ? null : renderPositionNext()}

			<Collapse in={_step === 'profile'} animateOpacity>
				<>
					<RadioGroup defaultValue='individual'>
						<FormLabel>{t('profile.status')}</FormLabel>
						<Stack direction={isMobile ? 'column' : 'row'} spacing={5} mt={1}>
							<Box
								border={_accType === 'individual' ? '2px' : 0}
								borderColor={'geoBlue.500'}
								borderRadius={5}
								padding={3}
							>
								<GeoRadio
									id='individual-radio'
									name='accType'
									value='individual'
									label={t('profile.personalStatus')}
									dataCy='radio-acctype-individual'
								/>
							</Box>
							<Box
								border={_accType === 'corporate' ? '2px' : 0}
								borderColor={'geoBlue.500'}
								borderRadius={5}
								padding={3}
							>
								<GeoRadio
									id='corporate-radio'
									name='accType'
									value='corporate'
									label={t('profile.professionalStatus')}
									dataCy='radio-acctype-corporate'
								/>
							</Box>
						</Stack>
					</RadioGroup>

					<Box marginTop={4}>
						<GeoInput
							name='firstName'
							label={t('profile.account_firstName')}
							type='text'
							placeholder={t('profile.placeholder_first_name')}
							data-cy='input-firstName'
							validation={required('errors.required')}
						/>
					</Box>
					<Box marginTop={4}>
						<GeoInput
							name='lastName'
							label={t('profile.account_lastName')}
							type='text'
							placeholder={t('profile.placeholder_last_name')}
							data-cy='input-lastName'
							validation={required('errors.required')}
						/>
					</Box>
					<Collapse in={_accType === 'individual'} animateOpacity>
						<Box marginTop={4}>
							<GeoCheckbox
								name='rgpd'
								label={t('profile.infoPermission')}
								data-cy='checkbox-rgpd'
							/>
						</Box>
					</Collapse>
					<FormStatusError error={error} />
					{renderPosition()}
				</>
			</Collapse>
		</Stack>
	);
};

export const SignupForm: React.FC = () => {
	const {signupOperation} = useSignup();
	const onSubmitForm = (data: LocalSignupForm) => {
		return signupOperation.execute({...data});
	};

	return (
		<Form<LocalSignupForm>
			defaultValues={{
				step: 'credentials',
				accType: 'individual'
			}}
			onSubmit={onSubmitForm}
		>
			<GeoHiddenField name='step' type='hidden' />
			<SignupFormContent
				loading={signupOperation.loading}
				error={signupOperation.error}
			/>
		</Form>
	);
};
